import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useDepartments } from "@/contexts/DepartmentsContext";
import {
  Sparkles,
  Upload,
  Camera,
  Loader2,
  CheckCircle2,
  X,
  ArrowLeft,
  Monitor,
  Boxes,
  Truck,
  Cog,
  Wrench,
  ChevronRight,
} from "lucide-react";

/**
 * SnapImport — one hub for all Snap-to-Import flows.
 *
 * The Assets flow already exists as `/walkaround-import` because it has
 * bespoke qty-expansion logic (one row → N discrete asset records). This
 * page keeps that flow intact and adds three parallel workflows for
 * Inventory, Suppliers and Machinery — same "snap → parse → review →
 * bulk commit" pattern, different column sets, no qty expansion since a
 * stock line / supplier / machine is always 1:1.
 *
 * Commit strategy: iterate rows and POST to the existing individual
 * create endpoints (`/stock`, `/suppliers`, `/machines`). Sequential so
 * we surface a clear error per row without blowing through rate limits,
 * and small enough batches (Gemini caps around 30 rows per photo) that
 * the manager can wait ~15s for the whole set to land.
 */

// smartCase — Gemini echoes source casing, so a docket with
// "DELL LATITUDE 5540" comes back in caps. Convert fully-uppercase
// strings to Title Case, but leave anything with lowercase letters
// alone so intentional casing ("HP", "iPad", "3M") survives.
const smartCase = (s) => {
  if (!s || typeof s !== "string") return s;
  if (/[a-z]/.test(s)) return s;
  return s
    .toLowerCase()
    .replace(/(^|[\s-/])(\w)/g, (_, sep, ch) => sep + ch.toUpperCase());
};

const normaliseRow = (r) => {
  // Fields where casing matters (emails, URLs, serials, phone) get
  // preserved verbatim. Everything else runs through smartCase.
  const KEEP_VERBATIM = new Set([
    "email",
    "phone",
    "website",
    "serial",
    "abn",
    "purchased_at",
  ]);
  const out = { ...r };
  Object.keys(out).forEach((k) => {
    if (typeof out[k] === "string" && !KEEP_VERBATIM.has(k)) {
      out[k] = smartCase(out[k]);
    }
  });
  return out;
};

// ── Column config per kind. Keeps the shared table renderer thin. ──
const KIND_CONFIG = {
  inventory: {
    label: "Inventory",
    subtitle: "Snap a delivery docket, packing list or handwritten stock list.",
    icon: Boxes,
    accent: "#5B3A99",
    parseUrl: "/stock/snap-parse",
    createUrl: "/stock",
    successRoute: "/catalog",
    columns: [
      { key: "name", label: "Name", w: "20%", required: true },
      { key: "category", label: "Category", w: "10%" },
      { key: "material", label: "Material", w: "10%" },
      { key: "unit", label: "Unit", w: "7%" },
      { key: "current_qty", label: "Qty", w: "6%", type: "number" },
      { key: "unit_cost", label: "Unit $", w: "8%", type: "number" },
      { key: "pack_size", label: "Pack size", w: "8%", type: "number" },
      { key: "pack_unit", label: "Pack unit", w: "8%" },
      { key: "supplier_name", label: "Supplier", w: "10%" },
      { key: "bin_location", label: "Bin", w: "6%" },
      { key: "notes", label: "Notes", w: "7%" },
    ],
    emptyRow: () => ({
      picked: true,
      name: "",
      category: "",
      material: "",
      unit: "unit",
      current_qty: 1,
      unit_cost: 0,
      pack_size: "",
      pack_unit: "",
      supplier_name: "",
      bin_location: "",
      notes: "",
    }),
    toBody: (r) => ({
      name: r.name,
      type: "material",
      category: r.category || null,
      material: r.material || null,
      unit: r.unit || "unit",
      current_qty: Number(r.current_qty) || 0,
      unit_cost: Number(r.unit_cost) || 0,
      pack_size: r.pack_size !== "" ? Number(r.pack_size) || null : null,
      pack_unit: r.pack_unit || null,
      supplier_default: r.supplier_name || null,
      bin_location: r.bin_location || null,
      notes: r.notes || null,
      reorder_weeks: 4,
    }),
  },
  suppliers: {
    label: "Suppliers",
    subtitle: "Snap a business card, letterhead, email signature or contact list.",
    icon: Truck,
    accent: "#228EBF",
    parseUrl: "/suppliers/snap-parse",
    createUrl: "/suppliers",
    successRoute: "/suppliers",
    columns: [
      { key: "name", label: "Company", w: "20%", required: true },
      { key: "email", label: "Email", w: "16%" },
      { key: "phone", label: "Phone", w: "12%" },
      { key: "website", label: "Website", w: "14%" },
      { key: "contact_name", label: "Contact", w: "12%" },
      { key: "abn", label: "ABN", w: "10%" },
      { key: "address", label: "Address", w: "8%" },
      { key: "notes", label: "Notes", w: "8%" },
    ],
    emptyRow: () => ({
      picked: true,
      name: "",
      email: "",
      phone: "",
      website: "",
      contact_name: "",
      abn: "",
      address: "",
      notes: "",
    }),
    toBody: (r) => ({
      name: r.name,
      email: r.email || null,
      phone: r.phone || null,
      website: r.website || null,
      contact_name: r.contact_name || null,
      abn: r.abn || null,
      address: r.address || null,
      notes: r.notes || null,
    }),
  },
  machinery: {
    label: "Machinery",
    subtitle: "Snap an asset register, nameplate photo or machinery invoice.",
    icon: Cog,
    accent: "#8A1F1F",
    parseUrl: "/machines/snap-parse",
    createUrl: "/machines",
    successRoute: "/machinery",
    columns: [
      { key: "name", label: "Name", w: "18%", required: true },
      { key: "category", label: "Category", w: "12%" },
      { key: "manufacturer", label: "Manufacturer", w: "12%" },
      { key: "model", label: "Model", w: "12%" },
      { key: "serial", label: "Serial", w: "12%" },
      { key: "cost", label: "Cost $", w: "8%", type: "number" },
      { key: "purchased_at", label: "Purchased", w: "10%" },
      { key: "department", label: "Dept", w: "8%", dept: true },
      { key: "notes", label: "Notes", w: "8%" },
    ],
    emptyRow: () => ({
      picked: true,
      name: "",
      category: "",
      manufacturer: "",
      model: "",
      serial: "",
      cost: 0,
      purchased_at: "",
      department: "",
      notes: "",
    }),
    toBody: (r) => ({
      name: r.name,
      category: r.category || null,
      manufacturer: r.manufacturer || null,
      model: r.model || null,
      serial: r.serial || null,
      cost: Number(r.cost) || null,
      purchased_at: r.purchased_at || null,
      department: r.department || null,
      notes: r.notes || null,
    }),
  },
  providers: {
    label: "Providers",
    subtitle:
      "Snap a service quote, price list or letterhead — Pest Control, HVAC, Fire Safety, Electrician…",
    icon: Wrench,
    accent: "#B8611A",
    parseUrl: "/site-services/providers/snap-parse",
    createUrl: "/site-services/providers",
    successRoute: "/site-services",
    columns: [
      { key: "name", label: "Company", w: "18%", required: true },
      { key: "kind", label: "Kind", w: "12%", metaKey: "kinds", required: true, canAdd: { url: "/site-services/kinds", metaUrl: "/site-services/meta" } },
      { key: "interval", label: "Interval", w: "10%", metaKey: "intervals" },
      { key: "contact_name", label: "Contact", w: "10%" },
      { key: "phone", label: "Phone", w: "10%" },
      { key: "email", label: "Email", w: "14%" },
      { key: "website", label: "Website", w: "10%" },
      { key: "cost_per_visit", label: "Cost / visit $", w: "8%", type: "number" },
      { key: "first_visit_date", label: "First visit", w: "8%" },
    ],
    emptyRow: () => ({
      picked: true,
      name: "",
      kind: "",
      interval: "",
      contact_name: "",
      phone: "",
      email: "",
      website: "",
      cost_per_visit: 0,
      first_visit_date: "",
      notes: "",
    }),
    toBody: (r) => ({
      name: r.name,
      kind: r.kind || null,
      interval: r.interval || null,
      contact_name: r.contact_name || null,
      phone: r.phone || null,
      email: r.email || null,
      website: r.website || null,
      cost_per_visit:
        r.cost_per_visit === "" || r.cost_per_visit == null
          ? null
          : Number(r.cost_per_visit) || null,
      first_visit_date: r.first_visit_date || null,
      notes: r.notes || null,
    }),
  },
};

// ── Kind picker landing view ─────────────────────────────────────────
const KindPicker = ({ onPick }) => {
  const nav = useNavigate();
  const cards = [
    {
      id: "assets",
      label: "Assets",
      subtitle:
        "Walk-around sheet with laptops, chairs, ladders (single row → N units).",
      icon: Monitor,
      accent: "#4DA86A",
      // Assets already has bespoke qty-expansion + a print sheet, so route
      // to the existing purpose-built flow rather than duplicate it here.
      handler: () => nav("/walkaround-import"),
    },
    { id: "inventory", ...KIND_CONFIG.inventory },
    { id: "suppliers", ...KIND_CONFIG.suppliers },
    { id: "machinery", ...KIND_CONFIG.machinery },
    { id: "providers", ...KIND_CONFIG.providers },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <button
            key={c.id}
            type="button"
            data-testid={`snap-pick-${c.id}`}
            onClick={c.handler || (() => onPick(c.id))}
            className="nb-card p-6 text-left flex items-center gap-4 hover:translate-y-[-2px] transition-transform"
            style={{ borderColor: c.accent }}
          >
            <div
              className="p-3 rounded-xl border-2 border-black shrink-0"
              style={{ background: `${c.accent}20` }}
            >
              <Icon size={28} strokeWidth={2.5} style={{ color: c.accent }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-eyebrow" style={{ color: c.accent }}>
                Snap to Import
              </div>
              <div className="font-display text-2xl">{c.label}</div>
              <div className="text-xs text-[#607087] mt-1">{c.subtitle}</div>
            </div>
            <ChevronRight
              size={20}
              strokeWidth={2.5}
              className="text-[#607087] shrink-0"
            />
          </button>
        );
      })}
    </div>
  );
};

// ── Shared workflow view (upload → parse → edit → commit) ─────────────
const KindWorkflow = ({ kindId, onBack }) => {
  const cfg = KIND_CONFIG[kindId];
  const { departments } = useDepartments();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [rowErrors, setRowErrors] = useState({}); // pickedIndex → error text
  // Meta options for enum-backed columns (e.g. Provider kind + interval).
  // Only fetched when the current kind has at least one metaKey column.
  const [meta, setMeta] = useState({});
  useEffect(() => {
    const needs = cfg.columns.some((c) => c.metaKey);
    if (!needs) return;
    if (kindId === "providers") {
      api
        .get("/site-services/meta")
        .then(({ data }) => setMeta(data || {}))
        .catch(() => setMeta({}));
    }
  }, [kindId, cfg]);

  const handleFile = async (file) => {
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setBusy(true);
    setRows([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(cfg.parseUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const empty = cfg.emptyRow();
      const parsed = (data.rows || []).map((r) => ({
        ...empty,
        ...normaliseRow(r),
        picked: true,
      }));
      setRows(parsed);
      toast.success(
        `Read ${parsed.length} ${cfg.label.toLowerCase()} row${parsed.length === 1 ? "" : "s"}`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the photo");
    } finally {
      setBusy(false);
    }
  };

  const patch = (i, k, v) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const addMetaValue = async (col, rowIndex) => {
    // eslint-disable-next-line no-alert
    const raw = window.prompt(`New ${col.label.toLowerCase()}:`, "");
    if (raw == null) return;
    const name = raw.trim();
    if (!name) return;
    try {
      const { data } = await api.post(col.canAdd.url, { name });
      // Refresh meta so the new value appears in every row's dropdown.
      const { data: freshMeta } = await api.get(col.canAdd.metaUrl);
      setMeta(freshMeta || {});
      patch(rowIndex, col.key, data.name);
      toast.success(data.created ? `Added "${data.name}"` : `Already had "${data.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add");
    }
  };

  const removeRow = (i) =>
    setRows((rs) => rs.filter((_, idx) => idx !== i));

  const addBlankRow = () =>
    setRows((rs) => [...rs, cfg.emptyRow()]);

  const importAll = async () => {
    const picked = rows
      .map((r, idx) => ({ r, idx }))
      .filter(({ r }) => r.picked && String(r.name || "").trim());
    if (!picked.length) return toast.error("Tick at least one row to import");
    setImporting(true);
    setProgress({ done: 0, total: picked.length });
    setRowErrors({});
    const nextErrors = {};
    let ok = 0;
    let fail = 0;
    for (let i = 0; i < picked.length; i += 1) {
      const { r, idx } = picked[i];
      try {
        await api.post(cfg.createUrl, cfg.toBody(r));
        ok += 1;
      } catch (e) {
        fail += 1;
        const raw = e.response?.data?.detail;
        // Server sends `detail` as either a string or a validation array —
        // surface a compact human-readable line so the user knows exactly
        // which field to fix (e.g. "kind must be one of …").
        const msg =
          typeof raw === "string"
            ? raw
            : Array.isArray(raw)
              ? raw.map((x) => x.msg || JSON.stringify(x)).join("; ")
              : e.message || "failed";
        nextErrors[idx] = msg;
      }
      setProgress({ done: i + 1, total: picked.length });
    }
    setImporting(false);
    setRowErrors(nextErrors);
    if (fail === 0) {
      toast.success(`Imported ${ok} ${cfg.label.toLowerCase()} record${ok === 1 ? "" : "s"}`);
      nav(cfg.successRoute);
    } else {
      toast.error(
        `Imported ${ok}, ${fail} failed — see red rows below`,
      );
    }
  };

  const pickedCount = useMemo(
    () => rows.filter((r) => r.picked && String(r.name || "").trim()).length,
    [rows],
  );

  return (
    <>
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <button
          onClick={onBack}
          className="nb-btn bg-white p-2"
          aria-label="back"
          data-testid="snap-workflow-back"
        >
          <ArrowLeft size={16} strokeWidth={2.5} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow" style={{ color: cfg.accent }}>
            Snap to Import
          </div>
          <h1 className="font-display text-3xl leading-tight">{cfg.label}</h1>
          <p className="text-sm text-[#607087] mt-1">{cfg.subtitle}</p>
        </div>
      </div>

      {rows.length === 0 && (
        <div
          className="nb-card p-8 text-center max-w-2xl mx-auto"
          data-testid="snap-upload-panel"
        >
          {preview ? (
            <img
              src={preview}
              alt="upload preview"
              className="max-h-80 mx-auto rounded-lg border-2 border-black mb-4"
            />
          ) : (
            <div className="text-[#607087] mb-4">
              Landscape shots work best. Make sure headings and rows are visible
              and any handwriting is roughly in focus.
            </div>
          )}
          <div className="flex flex-wrap gap-2 justify-center">
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="snap-camera-input"
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="snap-file-input"
            />
            <button
              data-testid="snap-camera-btn"
              onClick={() => cameraRef.current?.click()}
              disabled={busy}
              className="nb-btn nb-btn-primary px-4 py-3 flex items-center gap-2"
            >
              <Camera size={16} strokeWidth={2.5} />
              {busy ? "Reading photo…" : "Take photo"}
            </button>
            <button
              data-testid="snap-choose-btn"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="nb-btn bg-white px-4 py-3 flex items-center gap-2"
            >
              {busy ? (
                <Loader2 size={16} className="animate-spin" strokeWidth={2.5} />
              ) : (
                <Upload size={16} strokeWidth={2.5} />
              )}
              {busy ? "Reading photo…" : "Choose photo"}
            </button>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="nb-card p-0 overflow-x-auto mb-4">
            <table
              className="w-full text-sm"
              style={{ minWidth: 1200 }}
              data-testid="snap-review-table"
            >
              <thead className="bg-[#F4F8FB] border-b-2 border-black">
                <tr className="text-left">
                  <th className="p-3 w-10"></th>
                  {cfg.columns.map((c) => (
                    <th
                      key={c.key}
                      className="p-3 label-eyebrow"
                      style={{ width: c.w }}
                    >
                      {c.label}
                    </th>
                  ))}
                  <th className="p-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const err = rowErrors[i];
                  return (
                  <React.Fragment key={i}>
                  <tr
                    data-testid={`snap-row-${i}`}
                    className={`border-b border-black/10 ${!r.picked ? "opacity-40" : ""} ${err ? "bg-red-50" : ""}`}
                  >
                    <td className="p-2 text-center">
                      <input
                        type="checkbox"
                        data-testid={`snap-row-picked-${i}`}
                        checked={!!r.picked}
                        onChange={(e) => patch(i, "picked", e.target.checked)}
                        className="h-5 w-5"
                      />
                    </td>
                    {cfg.columns.map((c) => {
                      const options = c.metaKey ? (meta[c.metaKey] || []) : null;
                      const currentVal = r[c.key] ?? "";
                      // If Gemini returned a value that isn't in the enum,
                      // flag the cell red so the user knows to pick a valid
                      // one before saving.
                      const bad =
                        options &&
                        currentVal &&
                        !options.includes(currentVal);
                      return (
                      <td key={c.key} className="p-2 align-top">
                        {c.dept ? (
                          <select
                            className="qv-input"
                            value={r[c.key] || ""}
                            onChange={(e) => patch(i, c.key, e.target.value)}
                          >
                            <option value="">— none —</option>
                            {(departments || []).map((d) => (
                              <option key={d.id} value={d.name}>
                                {d.name}
                              </option>
                            ))}
                          </select>
                        ) : options ? (
                          <div className="flex gap-1">
                          <select
                            data-testid={`snap-row-${c.key}-${i}`}
                            className={`qv-input ${bad ? "border-red-500 bg-red-50" : ""}`}
                            value={options.includes(currentVal) ? currentVal : ""}
                            onChange={(e) => patch(i, c.key, e.target.value)}
                          >
                            <option value="">
                              {c.required ? "— pick one —" : "— none —"}
                            </option>
                            {options.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                          {c.canAdd && (
                            <button
                              type="button"
                              data-testid={`snap-row-${c.key}-add-${i}`}
                              onClick={() => addMetaValue(c, i)}
                              title="Add new"
                              className="nb-btn bg-white px-2 shrink-0"
                            >
                              +
                            </button>
                          )}
                          </div>
                        ) : (
                          <input
                            type={c.type === "number" ? "number" : "text"}
                            step={c.type === "number" ? "0.01" : undefined}
                            className={`qv-input ${
                              c.type === "number" ? "font-mono-num" : ""
                            }`}
                            value={r[c.key] ?? ""}
                            onChange={(e) => patch(i, c.key, e.target.value)}
                            placeholder={c.required ? "required" : ""}
                          />
                        )}
                      </td>
                      );
                    })}
                    <td className="p-2 text-right">
                      <button
                        data-testid={`snap-row-remove-${i}`}
                        onClick={() => removeRow(i)}
                        className="nb-btn bg-white p-1.5"
                        aria-label="remove"
                      >
                        <X size={12} strokeWidth={2.5} />
                      </button>
                    </td>
                  </tr>
                  {err && (
                    <tr className="border-b border-red-200 bg-red-50" data-testid={`snap-row-error-${i}`}>
                      <td></td>
                      <td colSpan={cfg.columns.length + 1} className="p-2 text-xs text-red-700">
                        <b>Import failed:</b> {err}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={addBlankRow}
              className="nb-btn bg-white px-3 py-1.5 text-xs"
              data-testid="snap-add-row"
            >
              + Add blank row
            </button>
            <div className="text-xs text-[#607087]">
              Missed something? Type it in.
            </div>
          </div>

          <div className="sticky bottom-4 nb-card p-4 flex items-center justify-between shadow-lg flex-wrap gap-2">
            <div className="text-sm">
              <b>{pickedCount}</b> row{pickedCount === 1 ? "" : "s"} ticked ·
              will create <b>{pickedCount}</b> {cfg.label.toLowerCase()} record
              {pickedCount === 1 ? "" : "s"}
              {importing && (
                <span className="ml-2 text-[#607087]">
                  · saving {progress.done}/{progress.total}…
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={onBack}
                className="nb-btn bg-white px-4 py-2"
                disabled={importing}
              >
                Cancel
              </button>
              <button
                data-testid="snap-import-btn"
                onClick={importAll}
                disabled={importing || pickedCount === 0}
                className="nb-btn nb-btn-primary px-5 py-2 flex items-center gap-2"
              >
                {importing ? (
                  <Loader2 size={14} className="animate-spin" strokeWidth={2.5} />
                ) : (
                  <CheckCircle2 size={14} strokeWidth={2.5} />
                )}
                {importing ? "Importing…" : `Import ${pickedCount}`}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

// ── Page shell ────────────────────────────────────────────────────────
const SnapImport = () => {
  const [kindId, setKindId] = useState(null);
  return (
    <div className="p-4 md:p-6 lg:p-8 pb-24" data-testid="snap-import-page">
      {!kindId && (
        <>
          <div className="mb-6 flex items-center gap-3">
            <Sparkles size={26} strokeWidth={2.5} className="text-[#5B3A99]" />
            <div>
              <h1 className="font-display text-4xl sm:text-5xl">Snap Import</h1>
              <p className="text-sm text-[#607087] mt-1 max-w-2xl">
                Photograph a docket, contact list or nameplate — the app reads
                it with Gemini vision, lets you review the rows, then commits
                them into the register in one hit.
              </p>
            </div>
          </div>
          <KindPicker onPick={setKindId} />
        </>
      )}
      {kindId && (
        <KindWorkflow kindId={kindId} onBack={() => setKindId(null)} />
      )}
    </div>
  );
};

export default SnapImport;
